import { Link } from "react-router-dom";

/* ── Abstract geometric decorations ── */
const FloatingNodes = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    {/* Top-left diamond */}
    <div
      className="absolute float-a border border-[#FFD100]/30"
      style={{ top: "18%", left: "8%", width: 28, height: 28, transform: "rotate(45deg)" }}
    />
    {/* Top-right large diamond */}
    <div
      className="absolute float-b border border-[#FFD100]/15"
      style={{ top: "12%", right: "10%", width: 52, height: 52, transform: "rotate(45deg)" }}
    />
    {/* Mid-left circle */}
    <div
      className="absolute float-c border border-[#FFD100]/20 rounded-full"
      style={{ top: "50%", left: "4%", width: 64, height: 64 }}
    />
    {/* Bottom-right small diamond */}
    <div
      className="absolute float-a border border-[#FFD100]/25"
      style={{ bottom: "22%", right: "6%", width: 20, height: 20, transform: "rotate(45deg)", animationDelay: "2s" }}
    />
    {/* Center-right ring */}
    <div
      className="absolute float-b border-2 border-[#FFD100]/10 rounded-full"
      style={{ top: "40%", right: "14%", width: 96, height: 96, animationDelay: "3s" }}
    />
    {/* Bottom-left horizontal line */}
    <div
      className="absolute float-c"
      style={{ bottom: "30%", left: "6%", width: 80, height: 1, background: "linear-gradient(90deg, transparent, rgba(255,209,0,0.4), transparent)", animationDelay: "1s" }}
    />
    {/* Dot cluster */}
    <div className="absolute" style={{ top: "25%", right: "22%", width: 5, height: 5, background: "#FFD100", borderRadius: "50%", opacity: 0.4 }} />
    <div className="absolute" style={{ top: "28%", right: "24%", width: 3, height: 3, background: "#FFD100", borderRadius: "50%", opacity: 0.25 }} />
    <div className="absolute" style={{ top: "23%", right: "20%", width: 3, height: 3, background: "#FFD100", borderRadius: "50%", opacity: 0.2 }} />
  </div>
);

const GridMotif = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="heroGrid" width="80" height="80" patternUnits="userSpaceOnUse">
          <path d="M 80 0 L 0 0 0 80" fill="none" stroke="#FFD100" strokeWidth="0.4" opacity="0.12" />
        </pattern>
        <radialGradient id="gridFade" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#000000" stopOpacity="0" />
          <stop offset="100%" stopColor="#000000" stopOpacity="1" />
        </radialGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#heroGrid)" />
      <rect width="100%" height="100%" fill="url(#gridFade)" />
    </svg>
    {/* Axis cross-hair */}
    <div className="absolute top-[42%] left-[15%] right-[15%] h-px bg-gradient-to-r from-transparent via-[#FFD100]/20 to-transparent" />
    <div className="absolute top-0 bottom-0 left-1/2 w-px bg-gradient-to-b from-transparent via-[#FFD100]/15 to-transparent" />
    <div
      className="absolute border border-[#FFD100]/50"
      style={{ top: "42%", left: "50%", width: 12, height: 12, transform: "translate(-50%, -50%) rotate(45deg)" }}
    />
  </div>
);

const HeroSection = () => (
  <section className="relative bg-black min-h-screen flex flex-col items-center justify-center overflow-hidden pt-20">
    {/* ── Radial gold glow bloom ── */}
    <div
      className="glow-pulse absolute pointer-events-none"
      style={{
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "min(900px, 90vw)",
        height: "min(900px, 90vw)",
        background: "radial-gradient(ellipse at center, rgba(255,209,0,0.13) 0%, rgba(255,209,0,0.03) 45%, transparent 70%)",
        borderRadius: "50%",
      }}
    />

    <GridMotif />
    <FloatingNodes />

    {/* ── Content ── */}
    <div className="relative z-10 text-center px-6 md:px-16 max-w-6xl mx-auto w-full">
      {/* Eyebrow */}
      <div className="hero-line-1 flex items-center justify-center gap-4 mb-8">
        <span className="block w-12 h-px bg-[#FFD100]" />
        <span
          className="text-[#FFD100] text-xs tracking-[4px] font-bold uppercase"
          style={{ fontFamily: "Arial, sans-serif" }}
        >
          AI HEALTH INFRASTRUCTURE
        </span>
        <span className="block w-12 h-px bg-[#FFD100]" />
      </div>

      {/* Headline */}
      <h1 style={{ fontFamily: "Arial, sans-serif" }}>
        <span
          className="hero-line-2 block text-white font-black uppercase"
          style={{ fontSize: "clamp(2.6rem, 6.5vw, 6rem)", letterSpacing: "-1.5px", lineHeight: "1.03" }}
        >
          BUILD THE SYSTEM
        </span>
        <span
          className="hero-line-3 block text-[#FFD100] font-black uppercase"
          style={{ fontSize: "clamp(2.6rem, 6.5vw, 6rem)", letterSpacing: "-1.5px", lineHeight: "1.03" }}
        >
          YOUR PATIENTS
        </span>
        <span
          className="hero-line-3 block text-white font-black uppercase"
          style={{ fontSize: "clamp(2.6rem, 6.5vw, 6rem)", letterSpacing: "-1.5px", lineHeight: "1.03", animationDelay: "0.8s" }}
        >
          EXPECT
        </span>
      </h1>

      {/* Thin gold rule */}
      <div className="hero-sub flex items-center justify-center gap-6 my-10" style={{ animationDelay: "1s" }}>
        <div className="h-px flex-1 max-w-[120px] bg-gradient-to-r from-transparent to-[#FFD100]/50" />
        <div className="w-1.5 h-1.5 rotate-45 bg-[#FFD100]/60 flex-shrink-0" />
        <div className="h-px flex-1 max-w-[120px] bg-gradient-to-l from-transparent to-[#FFD100]/50" />
      </div>

      {/* Subheadline */}
      <p
        className="hero-sub text-[#E5E5E5] text-base md:text-lg max-w-2xl mx-auto mb-12"
        style={{ fontFamily: "Arial, sans-serif", lineHeight: "1.75", animationDelay: "1.1s" }}
      >
        AI-powered infrastructure designed to capture, convert, and retain
        patients—without adding operational complexity.
      </p>

      {/* CTAs */}
      <div className="hero-cta flex flex-col sm:flex-row items-center justify-center gap-4">
        <Link
          to="/contact"
          className="group relative bg-[#FFD100] text-black text-xs font-black tracking-[3px] px-10 py-4 cursor-pointer whitespace-nowrap transition-all duration-300 hover:bg-white inline-flex items-center gap-3"
          style={{ fontFamily: "Arial, sans-serif" }}
        >
          START YOUR TRANSFORMATION
          <i className="ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
        <Link
          to="/industries"
          className="border border-white/25 text-white text-xs font-bold tracking-[3px] px-10 py-4 cursor-pointer whitespace-nowrap transition-all duration-300 hover:border-[#FFD100] hover:text-[#FFD100] inline-block backdrop-blur-sm"
          style={{ fontFamily: "Arial, sans-serif" }}
        >
          EXPLORE SYSTEMS
        </Link>
      </div>

      {/* Scroll indicator */}
      <div className="hero-scroll mt-20 flex flex-col items-center gap-2 opacity-30 caret-bob">
        <span className="text-white text-[10px] tracking-[3px]" style={{ fontFamily: "Arial, sans-serif" }}>SCROLL</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/70 to-transparent" />
        <i className="ri-arrow-down-line text-white/60 text-xs" />
      </div>
    </div>

    {/* Bottom gradient bleed */}
    <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" />
  </section>
);

export default HeroSection;
